import { makeObservable, computed, reaction, observable, runInAction } from 'mobx';
import { Disposer } from '../../custom_shared/misc';
import { InstanceUtils } from '../misc';
import { IInputsStoreBase } from '../../custom_shared/types';
import { InputBaseModel, NodeBaseModel } from '../models';
import NodesStore from './NodesStore';

export default class InputsStore extends Disposer implements IInputsStoreBase {
    highlightedInputGuid: string | null = null;

    highlightedInputTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly nodesStore: NodesStore) {
        super();

        makeObservable(this, {
            highlightedInputGuid: observable,
            inputs: computed,
            inputsWithSource: computed,
            inputsLoading: computed
        });

        this.reactions.push(
            reaction(
                () => this.inputsWithSource,
                inputsWithSource => {
                    inputsWithSource
                        .filter(input => !input.useCachedSourceOptions)
                        .forEach(input => input.fetchSourceOptions());
                }
            )
        );
    }

    setHighlightedInputGuid(highlightedInputGuid: string | null, timeout: number = 0) {
        if (this.highlightedInputTimeout) {
            clearTimeout(this.highlightedInputTimeout);
        }

        this.highlightedInputTimeout = setTimeout(() => {
            runInAction(() => {
                this.highlightedInputGuid = highlightedInputGuid;
            });
        }, timeout);
    }

    get inputs() {
        return this.nodesStore.nodes.flatMap(node => this.getNodeInputs(node));
    }

    get inputsWithSource() {
        return this.inputs.filter(InstanceUtils.isInputWithSource);
    }

    get inputsLoading() {
        return this.inputsWithSource.some(input => input.isLoading);
    }

    dispose() {
        this.disposeReactions();
    }

    private getNodeInputs(node: NodeBaseModel): InputBaseModel[] {
        return [...node.inputs, ...node.childNodes.flatMap(childNode => this.getNodeInputs(childNode))];
    }
}
